import { store } from '@/store'
import { Combatant, Character, Subtype, Stance, Bonuses } from '@/class'

class Spirit extends Combatant {
  private name_: string
  private traits_: Array<string>
  private spirit_type_: Subtype
  private weapons_: Array<string>
  private character_: Character
  private current_stance_: Stance

  public constructor() {
    super()
    this.spirit_type_ = undefined
    this.weapons_ = []
    this.name_ = ''
  }

  // ==========================================================
  // COMBATANT OVERRIDES
  // ==========================================================
  override get Block() {
    var block = this.current_stance_.Block + this.spirit_type_.Block
    for (var equipment of this.Equipment) {
      if (store.getters.isArmor(equipment.Name)) block += equipment.Block
    }
    return block
  }

  override get MaxSpeed() {
    var speed = this.spirit_type_.Speed + this.combinedBonuses_.Speed + this.current_stance_.Speed
    return speed
  }

  override get MaxGuard() {
    var guard = this.spirit_type_.Guard + this.combinedBonuses_.Guard
    for (var item of this.combinedBonuses_.Equipment) {
      if (store.getters.isArmor(item)) {
        guard += store.getters.getArmor(item).Guard
      }
    }
    return guard
  }

  override get Traits() {
    return this.traits_
  }

  override get MaxSoak() {
    var soak = 0
    for (var equipment of this.Equipment) {
      if (store.getters.isArmor(equipment.Name)) soak += equipment.Soak
    }
    return soak
  }

  override get Weapons() {
    return store.getters.getWeaponsFromList(this.weapons_)
  }

  override get Abilities() {
    var abilities = store.getters.basicAbilities
    abilities = abilities.concat(store.getters.spiritAbilities)
    return store.getters.getAbilitiesFromList(abilities)
  }

  get Arts() {
    var arts = this.character_.SpiritArts
    for (var trait of this.traits_) {
      arts.push(trait)
    }
    arts.sort()
    return arts
  }

  override get AllArts() {
    var arts = store.getters.getAPsFromListPreserveType([...this.Traits, ...this.character_.SpiritArts, this.character_.Element + 'born'])
    arts = arts.concat(this.Equipment)

    return arts
  }

  get Stances() {
    var stances = [...store.getters.basicSpiritStances]
    // Collect from Disciplines
    // Collect from Archetypes
    stances = store.getters.getStancesFromList(stances)
    for (var trait of store.getters.getAPsFromListPreserveType(this.traits_)) {
      if (trait.Stances.length > 0) stances = stances.concat(trait.Stances)
    }
    return stances
  }

  // ==========================================================
  // GETTERS/SETTERS
  // ==========================================================
  get Name() {
    return this.name_
  }
  set Name(name) {
    this.name_ = name
  }

  get Size() {
    if (this.Traits.includes('Tiny Form')) return 0.5
    if (this.Traits.includes('Small Form')) return 1
    if (this.Traits.includes('Medium Form')) return 2
    if (this.Traits.includes('Large Form')) return 3
    return 1
  }

  get GrowthPoints() {
    var gp = 0
    for (var trait of store.getters.getAPsFromListPreserveType(this.Traits)) {
      if (trait.HasCost && trait.Cost != 'None') {
        gp += Number(trait.Cost[0])
      }
    }
    return gp
  }

  set Character(character) {
    this.character_ = character
  }

  get CurrentStance() {
    return this.current_stance_
  }

  set CurrentStance(stance: Stance | string) {
    if (stance instanceof Stance) {
      this.current_stance_ = stance
    } else {
      this.current_stance_ = store.getters.getStance(stance)
    }
    if (this.Speed > this.MaxSpeed) this.Speed = this.MaxSpeed
  }

  override get MaxStamina() {
    return this.SpiritType.Stamina + this.combinedBonuses_.Stamina
  }

  get HasSpiritType() {
    return this.spirit_type_
  }

  get SpiritType() {
    return this.spirit_type_
  }

  get Phase() {
    return this.spirit_type_.Phase + this.combinedBonuses_.Phase
  }

  set SpiritType(input) {
    this.spirit_type_ = input
  }

  get Equipment() {
    var equipment = store.getters.getAnyEquipmentFromList(this.weapons_)
    if (this.combinedBonuses_ && this.combinedBonuses_.Equipment.length > 0) {
      equipment = equipment.concat(store.getters.getAnyEquipmentFromList(this.combinedBonuses_.Equipment))
    }

    return equipment
  }

  get UniqueEquipment() {
    return this.Equipment
  }

  // ==========================================================
  // HELPERS
  // ==========================================================

  get HasWeapons() {
    if (this.spirit_type_ == undefined) return false

    return true
  }

  private ClearWeapons() {
    this.weapons_ = []
  }

  // ==========================================================
  // SERIALIZATION
  // ==========================================================
  public static Serialize(spirit: Spirit): ISpiritData {
    return {
      ...super.Serialize(spirit),
      name: spirit.name_,
      type: spirit.spirit_type_.Name,
      weapons: spirit.weapons_,
      traits: spirit.traits_,
      current_stance: spirit.current_stance_.Name,
    }
  }

  public static CreateFromCharacter(char: Character, data: ISpiritData): Spirit {
    const c = new Spirit()
    c.character_ = char
    c.setSpiritData(data)
    return c
  }

  public static Deserialize(data: ISpiritData): Spirit {
    const c = new Spirit()
    c.setSpiritData(data)
    return c
  }

  private setSpiritData(data: ISpiritData): void {
    this.spirit_type_ = store.getters.getSpiritType(data.type)
    this.traits_ = data.traits || []
    this.name_ = data.name || ''
    this.weapons_ = data.weapons || []
    if ('current_stance' in data) this.current_stance_ = store.getters.getStance(data.current_stance)
    this.setBonuses()
    this.setCombatantData(data)
  }
}
export default Spirit
